import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import React from "react";
import API_ENDPOINTS from "../apiConfig";
import { bogeyNumbers } from "../helpers/dart-logic";
import { BulkHighlightRequest } from "../interfaces/Highlight";

interface HighlightsDialogProps {
  open: boolean;
  onClose: () => void;
  playermatchId: number;
  matchType: "singles" | "doubles";
  selectedPlayer: number | "";
  setSelectedPlayer: (id: number | "") => void;
  PlayerName?: string;
}

const initialHighscores = { "180": 0, "177": 0, "174": 0, "171": 0 };

const HighlightsDialog: React.FC<HighlightsDialogProps> = ({
  open,
  onClose,
  playermatchId,
  matchType,
  selectedPlayer,
  setSelectedPlayer,
  PlayerName,
}) => {
  const [highscores, setHighscores] = React.useState(initialHighscores);
  const [highfinishes, setHighfinishes] = React.useState<number[]>([0]);
  const [saving, setSaving] = React.useState(false);
  const [highfinishErrors, setHighfinishErrors] = React.useState<string[]>([]);

  // Track ids for highscores and highfinishes
  const [highscoreIds, setHighscoreIds] = React.useState<{
    [type: string]: number | null;
  }>({});
  const [highfinishObjs, setHighfinishObjs] = React.useState<
    Array<{ id: number | null; value: number }>
  >([]);

  const first180Ref = React.useRef<HTMLInputElement>(null);
  const highfinishRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const highscoreRefs = React.useRef<Array<HTMLInputElement | null>>([]);

  // Validation function for highfinishes
  const validateHighfinish = (value: number): string => {
    if (value === 0) return ""; // Allow 0 as it represents "no value"
    if (value < 101) return "Highfinish muss mindestens 101 sein";
    if (value > 170) return "Highfinish kann nicht höher als 170 sein";
    if (bogeyNumbers.includes(value))
      return `${value} ist eine Bogey-Zahl und nicht möglich`;
    return "";
  };
  React.useEffect(() => {
    if (open && selectedPlayer) {
      // Use setTimeout to ensure the DOM is ready and prioritize focus
      setTimeout(() => {
        if (first180Ref.current) {
          first180Ref.current.focus();
          first180Ref.current.select();
        }
      }, 100);
    }
  }, [open, selectedPlayer]);

  React.useEffect(() => {
    if (!open) {
      setHighscores(initialHighscores);
      setHighfinishes([]);
      setHighfinishErrors([]);
      setHighscoreIds({});
      setHighfinishObjs([]);
    }
  }, [open]);

  React.useEffect(() => {
    // Fetch highlights for the selected player when dialog opens and player is selected
    if (open && selectedPlayer) {
      const fetchHighlights = async () => {
        try {
          const url =
            matchType === "singles"
              ? API_ENDPOINTS.playerHighlights(playermatchId, selectedPlayer)
              : API_ENDPOINTS.playerDoubleMatchHighlights(playermatchId, selectedPlayer);
          const res = await fetch(url, { method: "GET" });
          if (!res.ok) return;
          const data = await res.json();
          if (data && typeof data === "object") {
            // Highscores: array of objects [{id, type, value}]
            console.log("fetched", data);
            if (Array.isArray(data.highscores)) {
              const newHighscores: typeof initialHighscores = {
                ...initialHighscores,
              };
              const newIds: { [type: string]: number | null } = {};
              data.highscores.forEach((hs: any) => {
                // If value is 180, 177, 174, or 171, treat as type
                let type: string | undefined = undefined;
                if (
                  typeof hs.value === "number" &&
                  [180, 177, 174, 171].includes(hs.value)
                ) {
                  type = String(hs.value);
                } else if (typeof hs.type === "string") {
                  type = hs.type;
                }
                if (
                  type &&
                  (type === "180" ||
                    type === "177" ||
                    type === "174" ||
                    type === "171")
                ) {
                  newHighscores[type as keyof typeof initialHighscores] =
                    hs.count ?? hs.value ?? 0;
                  newIds[type] = hs.id ?? null;
                }
              });
              setHighscores(newHighscores);
              setHighscoreIds(newIds);
            } else if (data.highscores) {
              setHighscores({ ...initialHighscores, ...data.highscores });
            }
            // Highfinishes: array of objects [{id, value}]
            if (Array.isArray(data.highfinishes)) {
              if (
                typeof data.highfinishes[0] === "object" &&
                data.highfinishes[0] !== null &&
                "value" in data.highfinishes[0]
              ) {
                setHighfinishObjs(
                  data.highfinishes.map((hf: any) => ({
                    id: hf.id ?? null,
                    value: hf.value,
                  }))
                );
                setHighfinishes(data.highfinishes.map((hf: any) => hf.value));
              } else {
                setHighfinishObjs(
                  data.highfinishes.map((v: number) => ({ id: null, value: v }))
                );
                setHighfinishes(data.highfinishes);
              }
            }
          }
        } catch (e) {
          // Optionally handle error
        }
      };
      fetchHighlights();
    }
  }, [open, selectedPlayer, playermatchId]);

  // Keep highfinishObjs in sync with highfinishes
  React.useEffect(() => {
    setHighfinishObjs((prev) => {
      // If lengths match, update values
      if (prev.length === highfinishes.length) {
        return prev.map((obj, idx) => ({ ...obj, value: highfinishes[idx] }));
      }
      // If new highfinishes added, add with null id
      if (highfinishes.length > prev.length) {
        return [
          ...prev,
          ...Array(highfinishes.length - prev.length)
            .fill(0)
            .map((_, i) => ({
              id: null,
              value: highfinishes[prev.length + i],
            })),
        ];
      }
      // If removed, slice
      return prev.slice(0, highfinishes.length);
    });
  }, [highfinishes]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: { xs: 18, sm: 20 }, textAlign: "center" }}>
        Highlight Details für {PlayerName || selectedPlayer}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ minWidth: { xs: 0, sm: 340 }, width: "100%" }}>
          {/* Only show highlight form if a player is selected */}
          {selectedPlayer && (
            <>
              {/* Highscore Inputs */}
              <Stack spacing={1}>
                {["180", "177", "174", "171"].map((type, idx) => (
                  <FormControl key={type} fullWidth size="small">
                    <TextField
                      label={type}
                      type="number"
                      inputProps={{ min: 0, style: { textAlign: "right" } }}
                      value={highscores[type as keyof typeof highscores]}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setHighscores((prev) => ({ ...prev, [type]: val }));
                        if (val === 0 && highscoreRefs.current[idx]) {
                          setTimeout(
                            () => highscoreRefs.current[idx]?.select(),
                            0
                          );
                        }
                      }}
                      onFocus={(e) => {
                        if (highscores[type as keyof typeof highscores] === 0) {
                          e.target.select();
                        }
                      }}
                      inputRef={(el) => {
                        highscoreRefs.current[idx] = el;
                        if (type === "180") first180Ref.current = el;
                      }}
                      size="small"
                      sx={{ mt: idx === 0 ? 1 : 0 }}
                    />
                  </FormControl>
                ))}
              </Stack>
              {/* Highfinish Section */}
              <Stack direction="row" spacing={1} alignItems="flex-start" mt={1}>
                <Stack spacing={1} flex={1}>
                  {(highfinishes.length === 0 ? [0] : highfinishes).map(
                    (val, idx) => {
                      const errorMessage = validateHighfinish(val);
                      return (
                        <FormControl key={idx} fullWidth size="small">
                          <TextField
                            label={`Highfinish #${idx + 1}`}
                            type="number"
                            inputProps={{
                              min: 0,
                              style: { textAlign: "right" },
                            }}
                            value={
                              val === 0 && highfinishes.length === 0 ? "" : val
                            }
                            inputRef={(el) => {
                              highfinishRefs.current[idx] = el;
                            }}
                            onFocus={(e) => {
                              if (val === 0) {
                                e.target.select();
                              }
                            }}
                            onChange={(e) => {
                              const newValue = Number(e.target.value);
                              if (highfinishes.length === 0) {
                                // First input - initialize the array
                                setHighfinishes([newValue]);
                                setHighfinishErrors([
                                  validateHighfinish(newValue),
                                ]);
                              } else {
                                // Update existing value
                                const arr = [...highfinishes];
                                arr[idx] = newValue;
                                setHighfinishes(arr);
                                // Update validation errors
                                const newErrors = [...highfinishErrors];
                                newErrors[idx] = validateHighfinish(newValue);
                                setHighfinishErrors(newErrors);
                              }
                              if (
                                newValue === 0 &&
                                highfinishRefs.current[idx]
                              ) {
                                setTimeout(
                                  () => highfinishRefs.current[idx]?.select(),
                                  0
                                );
                              }
                            }}
                            size="small"
                            placeholder={
                              highfinishes.length === 0 ? "z.B. 170" : undefined
                            }
                            className="no-spinner"
                            error={!!errorMessage}
                            helperText={errorMessage}
                          />
                        </FormControl>
                      );
                    }
                  )}
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: 12,
                    paddingInline: 5,
                    alignSelf: "flex-start",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    setHighfinishes((prev) => [...prev, 0]);
                    setHighfinishErrors((prev) => [...prev, ""]);
                  }}
                >
                  + weiteres Highfinish
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={saving}
          fullWidth
          sx={{ mr: 1 }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={async () => {
            if (!selectedPlayer) return;
            setSaving(true);
            let highscoresArr: any[];
            let highfinishesArr: any[];
            if (matchType === "singles") {
              highscoresArr = Object.keys(highscores)
                .map((type) => {
                  const obj: any = {
                    id:
                      highscoreIds[type] !== undefined
                        ? highscoreIds[type]
                        : null,
                    player: selectedPlayer,
                    type,
                    value: Number(type),
                    count: highscores[type as keyof typeof highscores] ?? 0,
                    playermatch: playermatchId,
                  };
                  // Remove playerdoublematch if present
                  delete obj.playerdoublematch;
                  return obj;
                })
                .filter((hs) => hs.count > 0 || (hs.id !== null && hs.id !== undefined));
              highfinishesArr = highfinishObjs
                .map((obj, idx) => {
                  const hfObj: any = {
                    id: obj.id !== undefined ? obj.id : null,
                    player: selectedPlayer,
                    value: highfinishes[idx] ?? 0,
                    playermatch: playermatchId,
                  };
                  delete hfObj.playerdoublematch;
                  return hfObj;
                })
                .filter((hf) => hf.value > 0 || (hf.id !== null && hf.id !== undefined));
            } else {
              highscoresArr = Object.keys(highscores)
                .map((type) => {
                  const obj: any = {
                    id:
                      highscoreIds[type] !== undefined
                        ? highscoreIds[type]
                        : null,
                    player: selectedPlayer,
                    type,
                    value: Number(type),
                    count: highscores[type as keyof typeof highscores] ?? 0,
                    playerdoublematch: playermatchId,
                  };
                  // Remove playermatch if present
                  delete obj.playermatch;
                  return obj;
                })
                .filter((hs) => hs.count > 0 || (hs.id !== null && hs.id !== undefined));
              highfinishesArr = highfinishObjs
                .map((obj, idx) => {
                  const hfObj: any = {
                    id: obj.id !== undefined ? obj.id : null,
                    player: selectedPlayer,
                    value: highfinishes[idx] ?? 0,
                    playerdoublematch: playermatchId,
                  };
                  delete hfObj.playermatch;
                  return hfObj;
                })
                .filter((hf) => hf.value > 0 || (hf.id !== null && hf.id !== undefined));
            }
            const data: BulkHighlightRequest = {
              highscores: highscoresArr,
              highfinishes: highfinishesArr,
            };
            try {
              const endpoint = API_ENDPOINTS.playerHighlights(playermatchId, selectedPlayer);
              const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
              });
              if (!response.ok) {
                throw new Error("Fehler beim Speichern der Highlights");
              }
              onClose();
              setSelectedPlayer("");
            } catch (e) {
              console.error("Error saving highlights:", e);
            } finally {
              setSaving(false);
              setHighscores(initialHighscores);
              setHighfinishes([]);
              setHighfinishErrors([]);
              setHighscoreIds({});
              setHighfinishObjs([]);
            }
          }}
          variant="contained"
          color="primary"
          disabled={
            saving ||
            !selectedPlayer ||
            ((Object.values(highscores).every((v) => !v) &&
              (!Array.isArray(highfinishes) ||
                highfinishes.length === 0 ||
                highfinishes.every((v) => !v))) &&
              Object.values(highscoreIds).every((id) => id === null || id === undefined) &&
              highfinishObjs.every((obj) => obj.id === null || obj.id === undefined)) ||
            highfinishErrors.some((error) => error !== "")
          }
          fullWidth
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HighlightsDialog;
